import { useCallback, useEffect, useRef } from 'react'
import { addGTMEvent } from 'api/googleTagManager'

interface UserTimingEvent {
  eventCategory: string
  label: string
  value: number
  variable: string
}

/**
 * Used to track performance events.
 * Ex.: Time to load JS, time for a component to appear.
 */
export function reportUserTimingEvent(event: UserTimingEvent) {
  addGTMEvent({
    event: 'ua-timing-event',
    eventVariable: event.variable,
    eventCategory: event.eventCategory,
    eventValue: event.value,
    eventLabel: event.label,
  })
}

export enum IdleTimeSource {
  FlashHotel = 'flash_hotel',
  BundleAndSave = 'bundle_and_save',
  AlwaysOn = 'always_on',
  HotelSearchPage = 'hotel_search_page',
  ExpiredDeals = 'expired_deals',
  DestionationLp = 'destination_lp'
}

const DEFAULT_THRESHOLD = 3000

// Events that indicate user activity (matching browser-interaction-time exactly)
// Window events use capture phase, document events use bubble phase
const WINDOW_ACTIVITY_EVENTS = ['scroll', 'resize'] as const
const DOCUMENT_ACTIVITY_EVENTS = [
  'wheel', 'keydown', 'keyup', 'mousedown', 'mousemove',
  'touchstart', 'touchmove', 'click', 'contextmenu',
] as const

/**
 * Hook to track user idle time and report it to analytics.
 * Replaces browser-interaction-time with a lightweight native implementation.
 */
function useIdleTimeReporter(label: IdleTimeSource, threshold: number = DEFAULT_THRESHOLD) {
  const activeRef = useRef(false)
  const lastInactiveTimeRef = useRef(0)
  const idleTimeoutRef = useRef<number | undefined>(undefined)
  const lastActivityRef = useRef(0)

  const handleIdle = useCallback(() => {
    if (activeRef.current) {
      lastInactiveTimeRef.current = Date.now()
      activeRef.current = false
    }
  }, [])

  const handleActivity = useCallback(() => {
    // Throttle: ignore activity within 2000ms of last handled activity
    const now = Date.now()
    if (now - lastActivityRef.current < 2000) {
      return
    }
    lastActivityRef.current = now

    // Reset the idle timeout
    window.clearTimeout(idleTimeoutRef.current)
    idleTimeoutRef.current = window.setTimeout(handleIdle, threshold)

    // If was idle, report the time
    if (!activeRef.current) {
      activeRef.current = true
      if (lastInactiveTimeRef.current > 0) {
        const timeIdle = now - lastInactiveTimeRef.current + threshold
        reportUserTimingEvent({
          eventCategory: 'lere',
          variable: 'timeIdle',
          value: timeIdle,
          label,
        })
        lastInactiveTimeRef.current = 0
      }
    }
  }, [threshold, label, handleIdle])

  useEffect(() => {
    // Match browser-interaction-time exactly:
    // - Window events (scroll, resize) use capture phase
    // - Document events use bubble phase with passive: true
    const windowOptions: AddEventListenerOptions = { passive: true, capture: true }
    const documentOptions: AddEventListenerOptions = { passive: true }

    WINDOW_ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, windowOptions)
    })
    DOCUMENT_ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, documentOptions)
    })

    // Start the initial idle timeout
    idleTimeoutRef.current = window.setTimeout(handleIdle, threshold)

    return () => {
      WINDOW_ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity, windowOptions)
      })
      DOCUMENT_ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
      window.clearTimeout(idleTimeoutRef.current)
    }
  }, [threshold, handleActivity, handleIdle])
}

export default useIdleTimeReporter
